import { DOM } from './state.js';
import { StaffReportesShell, StaffReportesOpciones, StaffReportesAviso } from './components/reportes/StaffReportesView.js';
import { AdminReportesView } from './components/reportes/AdminReportesView.js';
import { showToast } from './components/shared/Toast.js';
import {
    REPORTE_MAX_CHARS,
    largoReporte,
    validarReporte,
    loadEmpleadosParaReporte,
    enviarReporte,
    loadReportes,
    contarNuevos,
    marcar,
} from './services/staff-reportes.service.js';

/**
 * Staff reports (openspec/changes/staff-reportes/spec.md): staff "Reportes"
 * (`reportes`, send a signed message to Caro) and admin "Reportes"
 * (`reportes-admin`, read / archive), plus the nav badge with the count of
 * Nuevos. Registered in `SECTION_REGISTRY` (js/admin.js), same shape as
 * js/faq.js: each `mountReportes*Section(mount)` owns its state and
 * listeners, and every async result checks its section's `seq` (bumped by
 * each mount and each teardown) before painting.
 */

// ---------------------------------------------------------------------------
// Staff: send a message
// ---------------------------------------------------------------------------

const staff = {
    seq: 0,
    enviando: false,
};

const getStaffForm = () => DOM.adminContainer?.querySelector('form[data-action-form="reporte-enviar"]') ?? null;

/**
 * Entry point of the staff "Reportes" tab (`SECTION_REGISTRY.reportes.mount`).
 * @param {HTMLElement | null} mount
 */
export function mountReportesStaffSection(mount) {
    if (!mount) return;
    stopReportesStaff();
    staff.enviando = false;

    mount.innerHTML = StaffReportesShell({ maxChars: REPORTE_MAX_CHARS });
    const form = getStaffForm();
    form?.addEventListener('submit', handleStaffSubmit);
    form?.querySelector('textarea[name="texto"]')?.addEventListener('input', pintarContador);
    form?.addEventListener('click', handleStaffClick);

    void cargarEmpleados();
}

/** Teardown when leaving the tab or logging out: an in-flight response becomes stale. */
export function stopReportesStaff() {
    staff.seq += 1;
}

async function cargarEmpleados() {
    const seq = staff.seq;
    pintarOpciones({ status: 'loading', empleados: [] });
    pintarAviso(null);
    let empleados;
    try {
        empleados = await loadEmpleadosParaReporte();
    } catch (err) {
        if (seq !== staff.seq) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('reportes: no se pudo leer la lista de empleados', err);
        pintarOpciones({ status: 'error', empleados: [] });
        pintarAviso({ tipo: 'error', texto: 'No se pudieron cargar los nombres.', reintentar: true });
        return;
    }
    if (seq !== staff.seq) return;
    pintarOpciones({ status: 'ready', empleados });
}

/** @param {{ status: 'loading' | 'error' | 'ready', empleados: Array<{ id: string, nombre: string }> }} props */
function pintarOpciones(props) {
    const select = getStaffForm()?.querySelector('select[name="empleado_id"]');
    if (select) select.innerHTML = StaffReportesOpciones(props);
}

/** @param {{ tipo: 'ok' | 'error', texto: string, reintentar?: boolean } | null} aviso */
function pintarAviso(aviso) {
    const region = getStaffForm()?.querySelector('[data-reportes-aviso]');
    if (region) region.innerHTML = StaffReportesAviso(aviso);
}

function pintarContador() {
    const form = getStaffForm();
    const contador = form?.querySelector('[data-reportes-contador]');
    if (!contador) return;
    const largo = largoReporte(form.querySelector('textarea[name="texto"]')?.value);
    contador.textContent = `${largo} / ${REPORTE_MAX_CHARS}`;
    contador.classList.toggle('text-red-700', largo > REPORTE_MAX_CHARS);
    contador.classList.toggle('dark:text-red-300', largo > REPORTE_MAX_CHARS);
    contador.classList.toggle('text-text-muted', largo <= REPORTE_MAX_CHARS);
}

/** @param {MouseEvent} e */
function handleStaffClick(e) {
    const boton = /** @type {HTMLElement} */ (e.target).closest('[data-action="reportes-reintentar"]');
    if (boton) void cargarEmpleados();
}

/** @param {SubmitEvent} e */
async function handleStaffSubmit(e) {
    e.preventDefault();
    if (staff.enviando) return;
    const form = /** @type {HTMLFormElement} */ (e.currentTarget);
    const datos = {
        empleadoId: form.querySelector('select[name="empleado_id"]')?.value ?? '',
        pin: form.querySelector('input[name="pin"]')?.value ?? '',
        texto: form.querySelector('textarea[name="texto"]')?.value ?? '',
    };
    const validacion = validarReporte(datos);
    if (!validacion.ok) {
        pintarAviso({ tipo: 'error', texto: validacion.mensaje });
        return;
    }

    const seq = staff.seq;
    staff.enviando = true;
    const boton = form.querySelector('[data-reportes-enviar]');
    if (boton) boton.disabled = true;
    pintarAviso(null);

    const res = await enviarReporte(datos);
    if (seq !== staff.seq) return;
    staff.enviando = false;
    const actual = getStaffForm();
    if (!actual) return;
    const enviar = actual.querySelector('[data-reportes-enviar]');
    if (enviar) enviar.disabled = false;
    // The counter PC is shared: the PIN never stays typed after an attempt.
    const pin = actual.querySelector('input[name="pin"]');
    if (pin) pin.value = '';

    if (res.ok) {
        const texto = actual.querySelector('textarea[name="texto"]');
        if (texto) texto.value = '';
        const select = actual.querySelector('select[name="empleado_id"]');
        if (select) select.value = '';
        pintarContador();
        pintarAviso({ tipo: 'ok', texto: 'Mensaje enviado a Caro.' });
    } else {
        pintarAviso({ tipo: 'error', texto: res.mensaje });
    }
}

// ---------------------------------------------------------------------------
// Admin: read, mark read, archive
// ---------------------------------------------------------------------------

const admin = {
    seq: 0,
    loadSeq: 0,
    filtro: /** @type {'nuevos'|'leidos'|'archivados'} */ ('nuevos'),
    uiState: /** @type {{ status: 'loading' | 'error' | 'ready', mensaje?: string }} */ ({ status: 'loading' }),
    reportes: /** @type {Array<Record<string, any>>} */ ([]),
    marcando: /** @type {string | null} */ (null),
};

const FILTROS_VALIDOS = new Set(['nuevos', 'leidos', 'archivados']);

const getAdminMount = () => DOM.adminContainer?.querySelector('#reportes-admin-mount') ?? null;

/**
 * Entry point of the admin "Reportes" tab (`SECTION_REGISTRY['reportes-admin'].mount`).
 * @param {HTMLElement | null} mount
 */
export function mountReportesAdminSection(mount) {
    if (!mount) return;
    stopReportesAdmin();
    admin.filtro = 'nuevos';
    admin.uiState = { status: 'loading' };
    admin.reportes = [];
    admin.marcando = null;

    mount.innerHTML = '<div id="reportes-admin-mount"></div>';
    getAdminMount()?.addEventListener('click', handleAdminClick);
    void cargarAdmin();
}

/** Teardown when leaving the tab or logging out: an action in flight becomes stale. */
export function stopReportesAdmin() {
    admin.seq += 1;
}

function renderAdmin() {
    const region = getAdminMount();
    if (!region) return;
    region.innerHTML = AdminReportesView({
        uiState: admin.uiState,
        filtro: admin.filtro,
        reportes: admin.reportes,
        marcando: admin.marcando,
    });
}

async function cargarAdmin() {
    const seq = admin.seq;
    const loadSeq = ++admin.loadSeq;
    admin.uiState = { status: 'loading' };
    renderAdmin();
    try {
        // Assigned only after the guard: a slower, older load must not
        // replace the rows of the filter on screen (review owner-3).
        const reportes = await loadReportes({ filtro: admin.filtro });
        if (seq !== admin.seq || loadSeq !== admin.loadSeq) return;
        admin.reportes = reportes;
        admin.uiState = { status: 'ready' };
    } catch (err) {
        if (seq !== admin.seq || loadSeq !== admin.loadSeq) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('reportes: no se pudieron leer los mensajes', err);
        admin.reportes = [];
        admin.uiState = { status: 'error', mensaje: 'No se pudieron cargar los mensajes.' };
    }
    renderAdmin();
}

/** @param {MouseEvent} e */
function handleAdminClick(e) {
    const boton = /** @type {HTMLElement} */ (e.target).closest('[data-action]');
    if (!boton) return;
    const { action } = boton.dataset;
    if (action === 'reportes-filtro') {
        const filtro = boton.dataset.filtro;
        if (!FILTROS_VALIDOS.has(filtro) || filtro === admin.filtro) return;
        admin.filtro = /** @type {'nuevos'|'leidos'|'archivados'} */ (filtro);
        void cargarAdmin();
    } else if (action === 'reportes-reintentar') {
        void cargarAdmin();
    } else if (action === 'reporte-leido') {
        void handleMarcar(boton.dataset.id, 'leido');
    } else if (action === 'reporte-archivar') {
        void handleMarcar(boton.dataset.id, 'archivado');
    }
}

/**
 * @param {string | undefined} reporteId
 * @param {'leido'|'archivado'} accion
 */
async function handleMarcar(reporteId, accion) {
    if (!reporteId || admin.marcando) return;
    const seq = admin.seq;
    admin.marcando = reporteId;
    renderAdmin();
    const res = await marcar({ reporteId, accion });
    if (seq !== admin.seq) return;
    admin.marcando = null;
    if (!res.ok) showToast(res.mensaje, { level: 'error' });
    // A refusal (already read, gone) also means the list is out of date.
    await cargarAdmin();
    void refreshReportesBadge();
}

// ---------------------------------------------------------------------------
// Admin nav badge
// ---------------------------------------------------------------------------

let badgeSeq = 0;

const getBadge = () => DOM.adminContainer?.querySelector('[data-tab-badge="reportes-admin"]') ?? null;

/**
 * Paints the count of Nuevos on the admin nav item. Only the admin shell has
 * the slot, so without it nothing is read. A failed read hides the badge:
 * never a 0 that looks real (spec B3).
 */
export async function refreshReportesBadge() {
    if (!getBadge()) return;
    const seq = ++badgeSeq;
    let count = null;
    try {
        count = await contarNuevos();
    } catch (err) {
        if (seq !== badgeSeq) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('reportes: no se pudo contar los mensajes nuevos', err);
    }
    const badge = getBadge();
    if (seq !== badgeSeq || !badge) return;
    if (count == null || count <= 0) {
        badge.hidden = true;
        badge.textContent = '';
        badge.removeAttribute('aria-label');
        return;
    }
    badge.textContent = count > 99 ? '99+' : String(count);
    badge.setAttribute('aria-label', `${count} ${count === 1 ? 'mensaje nuevo' : 'mensajes nuevos'}`);
    badge.hidden = false;
}
